/**
 * 4:5 비율의 다운로드 이미지 생성 함수
 * (인스타그램 호환)
 */

import config from "lib/fr-config";

const colorCss = config.css.color;
const fontCss = config.css.fontThickness;
const fontFamilyCss = config.css.fontFamily;

const TEST_MODE = false;

const FONT_FAMILY = fontFamilyCss;

const RESULT_IMG_TYPE = "image/jpeg";
const RESULT_IMG_QUALITY = 0.92; // 원래 0~1중 0.92가 default래.;

const RESULT_IMG_RATIO = 4 / 5;
const RESULT_IMG_WIDTH_PX = 1080;

const INSTA_FEED_RATIO = 3 / 4;

const IMG_CROP_RATIO = 7 / 15;

/**
 * 이미지의 공백 설정 변수
 * */
/** 1:1로 잘랐을때의 이미지중 상하단의 공백의 높이 (전체 이미지의 폭을 기준으로한 길이) */
const TOP_BOTTOM_PADDING_RATIO_OF_INNER_SQUARE = 25 / 1000;
/** 좌우의 공백의 폭 (전체 이미지의 폭을 기준으로한 길이) */
const LEFT_RIGHT_PADDING_RATIO = 1 / 10;

const MAXIMUM_ROTATE_DEGREE = 40;
const MIN_OFFSET_X = 88.0;
const MAX_OFFSET_X = 115.6;
const MIN_OFFSET_Y = 16.9;
const MAX_OFFSET_Y = 149.6;

const LOGO_SVG_RATIO = 12250;

/**
 * 텍스트 배치 관련 공백 설정 변수
 */
const HORIZONTAL_PADDING_PERCENT = 10;
const VERTICAL_PADDING_PERCENT = 12;
const CONTENT_HEIGHT_PERCENT_SCORE = 20.6;
const CONTENT_HEIGHT_PERCENT_BRAND = 7.2;
const MAJOR_SCORE_FONT_SIZE_PERCENT = 12;
const MAJOR_BRAND_FONT_SIZE_PERCENT = 4.62;
const MINOR_FONT_SIZE_PERCENT = 2.22;
const PADDING_BETWEEN_MAJOR_AND_MINOR_PERCENT = 0.4;
const MINOR_TEXT_WEIGHT = fontCss.regular;
const MAJOR_TEXT_WEIGHT = fontCss.forScoreOfDownloadImage;

/**
 * ! WARNING ! WARNING ! WARNING ! WARNING ! WARNING ! WARNING !
 * ! WARNING ! WARNING ! WARNING ! WARNING ! WARNING ! WARNING !
 * ! WARNING ! WARNING ! WARNING ! WARNING ! WARNING ! WARNING !
 * 하단의 좌표보정값은 상단의 모든 변수가 변경되면 다시 보정해 줘야 한다.
 */
const LOGO_OFFSET_X = 10595;
const LOGO_OFFSET_Y = 840;
const LOGO_HEIGHT_RATIO = 0.02;

const HEART_OFFSET_X = 115.0;
const HEART_OFFSET_Y = 104.5;

const _getMajorTextFontSize = ({ widthOfResult, scoreMode }) =>
  scoreMode
    ? widthOfResult * (MAJOR_SCORE_FONT_SIZE_PERCENT / 100)
    : widthOfResult * (MAJOR_BRAND_FONT_SIZE_PERCENT / 100);

const _getMinorTextFontSize = ({ widthOfResult }) =>
  widthOfResult * (MINOR_FONT_SIZE_PERCENT / 100);

const _getNowTimeStr = () => {
  const now = new Date();

  const year = now.getFullYear();
  const month = (now.getMonth() + 1).toString().padStart(2, "0");
  const day = now.getDate().toString().padStart(2, "0");
  const hours = now.getHours().toString().padStart(2, "0");
  const minutes = now.getMinutes().toString().padStart(2, "0");
  const seconds = now.getSeconds().toString().padStart(2, "0");
  const milliseconds = now.getMilliseconds().toString().padStart(3, "0");

  return `${year}${month}${day}_${hours}${minutes}${seconds}_${milliseconds}`;
};

const _makeImageElement = src => {
  const returnImg = new Image();
  returnImg.crossOrigin = "anonymous";
  returnImg.src = src;
  return new Promise((resolve, reject) => {
    returnImg.onload = () => resolve(returnImg);
    returnImg.onerror = () =>
      reject(Error("CORS error while downloading image."));
  });
};

const _setCanvasWhite = ({ ctx }) => {
  ctx.fillStyle = colorCss.backgroundColorOfDownloadOutfit;
  ctx.fillRect(
    0,
    0,
    RESULT_IMG_WIDTH_PX,
    RESULT_IMG_WIDTH_PX / RESULT_IMG_RATIO
  );
};

const _setViewportForTest = ({ ctx }) => {
  const leftPaddingWidth = RESULT_IMG_WIDTH_PX * LEFT_RIGHT_PADDING_RATIO;
  const innerBottomPaddingOfSquareHeight =
    RESULT_IMG_WIDTH_PX * TOP_BOTTOM_PADDING_RATIO_OF_INNER_SQUARE;
  const topPaddingHeight =
    leftPaddingWidth -
    (RESULT_IMG_WIDTH_PX -
      (RESULT_IMG_WIDTH_PX / RESULT_IMG_RATIO) * INSTA_FEED_RATIO) /
      2;
  const wholeImgHeight =
    RESULT_IMG_WIDTH_PX / RESULT_IMG_RATIO - topPaddingHeight * 2;

  /** 상하 마진 */
  ctx.fillStyle = "rgba(0, 247, 0, 0.2)";
  ctx.fillRect(0, 0, RESULT_IMG_WIDTH_PX, topPaddingHeight);
  ctx.fillRect(
    0,
    wholeImgHeight + topPaddingHeight,
    RESULT_IMG_WIDTH_PX,
    topPaddingHeight
  );

  /** 좌우 마진 */
  ctx.fillStyle = "rgba(247, 0, 0, 0.2)";
  ctx.fillRect(0, 0, leftPaddingWidth, RESULT_IMG_WIDTH_PX / RESULT_IMG_RATIO);
  ctx.fillRect(
    RESULT_IMG_WIDTH_PX - leftPaddingWidth,
    0,
    leftPaddingWidth,
    RESULT_IMG_WIDTH_PX / RESULT_IMG_RATIO
  );

  /** 텍스트 컨텐츠를 위한 가운데 정사각형 파트 */
  ctx.fillStyle = "rgba(0, 0, 247, 0.2)";
  ctx.fillRect(
    0,
    (RESULT_IMG_WIDTH_PX / RESULT_IMG_RATIO - RESULT_IMG_WIDTH_PX) / 2,
    RESULT_IMG_WIDTH_PX,
    RESULT_IMG_WIDTH_PX
  );

  /** 정사각형 파트 하단의 내부 마진 */
  ctx.fillStyle = "rgba(0, 0, 0, 0.2)";
  ctx.fillRect(
    leftPaddingWidth,
    (RESULT_IMG_WIDTH_PX / RESULT_IMG_RATIO - RESULT_IMG_WIDTH_PX) / 2 +
      RESULT_IMG_WIDTH_PX -
      innerBottomPaddingOfSquareHeight,
    RESULT_IMG_WIDTH_PX - leftPaddingWidth * 2,
    innerBottomPaddingOfSquareHeight
  );
};

const _setOutfitImgOnCanvas = ({ ctx, outfitElem }) => {
  const { width, height } = outfitElem;
  const leftPaddingWidth = RESULT_IMG_WIDTH_PX * LEFT_RIGHT_PADDING_RATIO;
  const topPaddingHeight =
    leftPaddingWidth -
    (RESULT_IMG_WIDTH_PX -
      (RESULT_IMG_WIDTH_PX / RESULT_IMG_RATIO) * INSTA_FEED_RATIO) /
      2;
  const wholeImgHeight =
    RESULT_IMG_WIDTH_PX / RESULT_IMG_RATIO - topPaddingHeight * 2;

  /** 이미지를 크롭하는 좌표 */
  const imgCropWidth = height * IMG_CROP_RATIO;
  const imgCropHeight = height;
  const imgCropX = (width - imgCropWidth) / 2;
  const imgCropY = 0;

  /** 크롭한 이미지를 배치하는 좌표 */
  const canvasX = leftPaddingWidth;
  const canvasY = topPaddingHeight;
  const imgHeight = wholeImgHeight;
  const imgWidth = imgHeight * IMG_CROP_RATIO;

  ctx.drawImage(
    outfitElem,
    imgCropX,
    imgCropY,
    imgCropWidth,
    imgCropHeight,
    canvasX,
    canvasY,
    imgWidth,
    imgHeight
  );
};

const _setLogoOnCanvas = ({ ctx, offsetForAntiAliasing }) => {
  const widthOfResult = RESULT_IMG_WIDTH_PX;
  const scaleRatio = widthOfResult / LOGO_SVG_RATIO;
  ctx.scale(scaleRatio, scaleRatio);
  ctx.fillStyle = colorCss.textColorOfDownloadOutfit;
  const offsetX = offsetForAntiAliasing
    ? LOGO_OFFSET_X + offsetForAntiAliasing
    : LOGO_OFFSET_X;
  const offsetY = offsetForAntiAliasing
    ? LOGO_OFFSET_Y + offsetForAntiAliasing
    : LOGO_OFFSET_Y;
  ctx.beginPath();
  ctx.moveTo(392 + offsetX, 32 + offsetY);
  ctx.lineTo(369.373 + offsetX, 9.37258 + offsetY);
  ctx.bezierCurveTo(
    378.525 + offsetX,
    0.220648 + offsetY,
    392.288 + offsetX,
    -2.51714 + offsetY,
    404.246 + offsetX,
    2.43586 + offsetY
  );
  ctx.bezierCurveTo(
    416.203 + offsetX,
    7.38885 + offsetY,
    424 + offsetX,
    19.0572 + offsetY,
    424 + offsetX,
    32 + offsetY
  );
  ctx.lineTo(392 + offsetX, 32 + offsetY);
  ctx.closePath();
  ctx.moveTo(212 + offsetX, 32 + offsetY);
  ctx.lineTo(189.373 + offsetX, 9.37258 + offsetY);
  ctx.bezierCurveTo(
    201.869 + offsetX,
    -3.1242 + offsetY,
    222.131 + offsetX,
    -3.1242 + offsetY,
    234.627 + offsetX,
    9.37258 + offsetY
  );
  ctx.lineTo(212 + offsetX, 32 + offsetY);
  ctx.closePath();
  ctx.moveTo(122 + offsetX, 122 + offsetY);
  ctx.lineTo(144.627 + offsetX, 144.627 + offsetY);
  ctx.lineTo(122 + offsetX, 167.255 + offsetY);
  ctx.lineTo(99.3726 + offsetX, 144.627 + offsetY);
  ctx.lineTo(122 + offsetX, 122 + offsetY);
  ctx.closePath();
  ctx.moveTo(32 + offsetX, 32 + offsetY);
  ctx.lineTo(0 + offsetX, 32 + offsetY);
  ctx.bezierCurveTo(
    0 + offsetX,
    19.0572 + offsetY,
    7.79654 + offsetX,
    7.38885 + offsetY,
    19.7541 + offsetX,
    2.43586 + offsetY
  );
  ctx.bezierCurveTo(
    31.7117 + offsetX,
    -2.51714 + offsetY,
    45.4755 + offsetX,
    0.220648 + offsetY,
    54.6274 + offsetX,
    9.37258 + offsetY
  );
  ctx.lineTo(32 + offsetX, 32 + offsetY);
  ctx.closePath();
  ctx.moveTo(360 + offsetX, 244 + offsetY);
  ctx.lineTo(360 + offsetX, 32 + offsetY);
  ctx.lineTo(424 + offsetX, 32 + offsetY);
  ctx.lineTo(424 + offsetX, 244 + offsetY);
  ctx.lineTo(360 + offsetX, 244 + offsetY);
  ctx.closePath();
  ctx.moveTo(414.627 + offsetX, 54.6274 + offsetY);
  ctx.lineTo(324.627 + offsetX, 144.627 + offsetY);
  ctx.lineTo(279.373 + offsetX, 99.3726 + offsetY);
  ctx.lineTo(369.373 + offsetX, 9.37258 + offsetY);
  ctx.lineTo(414.627 + offsetX, 54.6274 + offsetY);
  ctx.closePath();
  ctx.moveTo(279.373 + offsetX, 144.627 + offsetY);
  ctx.lineTo(189.373 + offsetX, 54.6274 + offsetY);
  ctx.lineTo(234.627 + offsetX, 9.37258 + offsetY);
  ctx.lineTo(324.627 + offsetX, 99.3726 + offsetY);
  ctx.lineTo(279.373 + offsetX, 144.627 + offsetY);
  ctx.closePath();
  ctx.moveTo(234.627 + offsetX, 54.6274 + offsetY);
  ctx.lineTo(144.627 + offsetX, 144.627 + offsetY);
  ctx.lineTo(99.3726 + offsetX, 99.3726 + offsetY);
  ctx.lineTo(189.373 + offsetX, 9.37258 + offsetY);
  ctx.lineTo(234.627 + offsetX, 54.6274 + offsetY);
  ctx.closePath();
  ctx.moveTo(99.3726 + offsetX, 144.627 + offsetY);
  ctx.lineTo(9.37259 + offsetX, 54.6274 + offsetY);
  ctx.lineTo(54.6274 + offsetX, 9.37258 + offsetY);
  ctx.lineTo(144.627 + offsetX, 99.3726 + offsetY);
  ctx.lineTo(99.3726 + offsetX, 144.627 + offsetY);
  ctx.closePath();
  ctx.moveTo(64 + offsetX, 32 + offsetY);
  ctx.lineTo(64 + offsetX, 244 + offsetY);
  ctx.lineTo(0 + offsetX, 244 + offsetY);
  ctx.lineTo(0 + offsetX, 32 + offsetY);
  ctx.lineTo(64 + offsetX, 32 + offsetY);
  ctx.closePath();
  ctx.moveTo(324.627 + offsetX, 99.3726 + offsetY);
  ctx.lineTo(414.627 + offsetX, 189.373 + offsetY);
  ctx.lineTo(369.373 + offsetX, 234.627 + offsetY);
  ctx.lineTo(279.373 + offsetX, 144.627 + offsetY);
  ctx.lineTo(324.627 + offsetX, 99.3726 + offsetY);
  ctx.closePath();
  ctx.moveTo(324.627 + offsetX, 144.627 + offsetY);
  ctx.lineTo(243.127 + offsetX, 226.127 + offsetY);
  ctx.lineTo(197.873 + offsetX, 180.873 + offsetY);
  ctx.lineTo(279.373 + offsetX, 99.3726 + offsetY);
  ctx.lineTo(324.627 + offsetX, 144.627 + offsetY);
  ctx.closePath();
  ctx.fill();
  ctx.scale(1 / scaleRatio, 1 / scaleRatio);
};

const _setHeartOnCanvas = ({ ctx, scoreMode }) => {
  const widthOfResult = RESULT_IMG_WIDTH_PX;
  /** 하트 아이콘 시작 */
  const scaleRatio = widthOfResult / RESULT_IMG_RATIO / 190;
  const offsetX = scoreMode
    ? HEART_OFFSET_X
    : MIN_OFFSET_X + Math.random() * (MAX_OFFSET_X - MIN_OFFSET_X);
  const offsetY = scoreMode
    ? HEART_OFFSET_Y
    : MIN_OFFSET_Y + Math.random() * (MAX_OFFSET_Y - MIN_OFFSET_Y);
  const rotateDegree = MAXIMUM_ROTATE_DEGREE * (Math.random() * 2 - 1);

  ctx.scale(scaleRatio, scaleRatio);

  const heartCenterX = 12 + offsetX;
  const heartCenterY = 14 + offsetY;
  if (!scoreMode) {
    ctx.translate(heartCenterX, heartCenterY);
    ctx.rotate((rotateDegree * Math.PI) / 180);
    ctx.translate(-heartCenterX, -heartCenterY);
  }

  ctx.fillStyle = colorCss.colorOfVoteIcon;
  ctx.beginPath();
  ctx.moveTo(12 + offsetX, 4.923 + offsetY);
  ctx.bezierCurveTo(
    18.981 + offsetX,
    0.95 + offsetY,
    25.104 + offsetX,
    9.476 + offsetY,
    20.272 + offsetX,
    14.594 + offsetY
  );
  ctx.bezierCurveTo(
    19.188 + offsetX,
    15.743 + offsetY,
    16.973 + offsetX,
    17.495 + offsetY,
    16.256 + offsetX,
    17.945 + offsetY
  );
  ctx.bezierCurveTo(
    12.5 + offsetX,
    20.929 + offsetY,
    11.5 + offsetX,
    20.929 + offsetY,
    7.744 + offsetX,
    17.945 + offsetY
  );
  ctx.bezierCurveTo(
    7.027 + offsetX,
    17.495 + offsetY,
    4.812 + offsetX,
    15.743 + offsetY,
    3.727 + offsetX,
    14.594 + offsetY
  );
  ctx.bezierCurveTo(
    -1.104 + offsetX,
    9.477 + offsetY,
    5.019 + offsetX,
    0.95 + offsetY,
    12 + offsetX,
    4.923 + offsetY
  );
  ctx.closePath();
  ctx.fill();

  if (!scoreMode) {
    ctx.translate(heartCenterX, heartCenterY);
    ctx.rotate((-rotateDegree * Math.PI) / 180);
    ctx.translate(-heartCenterX, -heartCenterY);
  }

  ctx.scale(1 / scaleRatio, 1 / scaleRatio);
  /** 하트 아이콘 끝 */
};

const _setInfoTextOnCanvas = ({ ctx, dateCaptionStr, usernameStr }) => {
  let metrics;

  const widthOfResult = RESULT_IMG_WIDTH_PX;
  const heightOfResult = widthOfResult / RESULT_IMG_RATIO;
  const minorFontSize = _getMinorTextFontSize({ widthOfResult });
  const heightOfLogoAndMargin = widthOfResult * LOGO_HEIGHT_RATIO;

  /** 전체이미지의 좌우패팅 */
  const leftPaddingWidth = RESULT_IMG_WIDTH_PX * LEFT_RIGHT_PADDING_RATIO;

  /** 이미지 상단 패딩 */
  const topPaddingHeight =
    leftPaddingWidth -
    (RESULT_IMG_WIDTH_PX -
      (RESULT_IMG_WIDTH_PX / RESULT_IMG_RATIO) * INSTA_FEED_RATIO) /
      2;

  /** 사용자이름 캡션 시작 */
  ctx.fillStyle = colorCss.textColorOfDownloadOutfit;
  ctx.font = `${MAJOR_TEXT_WEIGHT} ${minorFontSize}px ${FONT_FAMILY}`;
  metrics = ctx.measureText(`@${usernameStr}`);
  ctx.fillText(
    `@${usernameStr}`,
    widthOfResult * ((100 - HORIZONTAL_PADDING_PERCENT) / 100) - metrics.width,
    topPaddingHeight +
      minorFontSize +
      heightOfResult * (PADDING_BETWEEN_MAJOR_AND_MINOR_PERCENT / 100) +
      heightOfLogoAndMargin
  );
  /** 사용자이름 캡션 끝 */

  /** 날짜 캡션 시작 */
  ctx.fillStyle = colorCss.textColorOfDownloadOutfit;
  ctx.font = `${MINOR_TEXT_WEIGHT} ${minorFontSize}px ${FONT_FAMILY}`;
  metrics = ctx.measureText(`${dateCaptionStr}`);
  ctx.fillText(
    `${dateCaptionStr}`,
    widthOfResult * ((100 - HORIZONTAL_PADDING_PERCENT) / 100) - metrics.width,
    topPaddingHeight +
      (minorFontSize +
        heightOfResult * (PADDING_BETWEEN_MAJOR_AND_MINOR_PERCENT / 100)) *
        2 +
      heightOfLogoAndMargin
  );
  /** 날짜 캡션 끝 */
};

const _setScoreOnCanvas = ({
  ctx,
  heartCaption1Str,
  heartCaption2Str,
  heartCaption3Str,
  scoreNum,
  scoreCaptionStr
}) => {
  let metrics;

  const widthOfResult = RESULT_IMG_WIDTH_PX;
  const heightOfResult = widthOfResult / RESULT_IMG_RATIO;
  const majorFontSize = _getMajorTextFontSize({
    widthOfResult,
    scoreMode: true
  });
  const minorFontSize = _getMinorTextFontSize({ widthOfResult });

  /** 하트 캡션 시작 */
  ctx.fillStyle = colorCss.textColorOfDownloadOutfit;
  ctx.font = `${MINOR_TEXT_WEIGHT} ${minorFontSize}px ${FONT_FAMILY}`;
  metrics = ctx.measureText(`${heartCaption1Str}`);
  ctx.fillText(
    `${heartCaption1Str}`,
    widthOfResult * ((100 - HORIZONTAL_PADDING_PERCENT) / 100) - metrics.width,
    heightOfResult * ((100 - VERTICAL_PADDING_PERCENT) / 100) -
      heightOfResult * (CONTENT_HEIGHT_PERCENT_SCORE / 100) * 1 + // 마지막 "* 1"의 값을 "* 2로 바꾸면" 한칸 위로 배치된다.
      heightOfResult * (PADDING_BETWEEN_MAJOR_AND_MINOR_PERCENT / 100)
  );
  /** 하트 캡션 끝 */

  /** 하트 캡션2 시작 */
  ctx.fillStyle = colorCss.textColorOfDownloadOutfit;
  ctx.font = `${MINOR_TEXT_WEIGHT} ${minorFontSize}px ${FONT_FAMILY}`;
  metrics = ctx.measureText(`${heartCaption2Str}`);
  ctx.fillText(
    `${heartCaption2Str}`,
    widthOfResult * ((100 - HORIZONTAL_PADDING_PERCENT) / 100) - metrics.width,
    heightOfResult * ((100 - VERTICAL_PADDING_PERCENT) / 100) -
      heightOfResult * (CONTENT_HEIGHT_PERCENT_SCORE / 100) * 1 + // "* 1"의 값을 "* 2로 바꾸면" 한칸 위로 배치된다.
      heightOfResult * (PADDING_BETWEEN_MAJOR_AND_MINOR_PERCENT / 100) * 2 +
      minorFontSize
  );
  /** 하트 캡션2 끝 */

  /** 하트 캡션3 시작 */
  ctx.fillStyle = colorCss.textColorOfDownloadOutfit;
  ctx.font = `${MINOR_TEXT_WEIGHT} ${minorFontSize}px ${FONT_FAMILY}`;
  metrics = ctx.measureText(`${heartCaption3Str}`);
  ctx.fillText(
    `${heartCaption3Str}`,
    widthOfResult * ((100 - HORIZONTAL_PADDING_PERCENT) / 100) - metrics.width,
    heightOfResult * ((100 - VERTICAL_PADDING_PERCENT) / 100) -
      heightOfResult * (CONTENT_HEIGHT_PERCENT_SCORE / 100) * 1 + // "* 1"의 값을 "* 2로 바꾸면" 한칸 위로 배치된다.
      heightOfResult * (PADDING_BETWEEN_MAJOR_AND_MINOR_PERCENT / 100) * 3 +
      minorFontSize * 2
  );
  /** 하트 캡션3 끝 */

  // /** 최하단 2번째 스코어 시작 */
  // ctx.fillStyle = colorCss.textColorOfDownloadOutfit;
  // ctx.font = `${MAJOR_TEXT_WEIGHT} ${majorFontSize}px ${FONT_FAMILY}`;
  // metrics = ctx.measureText(`${femaleScoreNum}`);
  // ctx.fillText(
  //   `${femaleScoreNum}`,
  //   widthOfResult * ((100 - HORIZONTAL_PADDING_PERCENT) / 100) - metrics.width,
  //   widthOfResult * ((100 - VERTICAL_PADDING_PERCENT) / 100) -
  //     widthOfResult * (CONTENT_HEIGHT_PERCENT_SCORE / 100) -
  //     minorFontSize -
  //     widthOfResult * (PADDING_BETWEEN_MAJOR_AND_MINOR_PERCENT / 100)
  // );
  // /** 최하단 2번째 스코어 끝 */

  // /** 최하단 2번째 스코어 캡션 시작 */
  // ctx.fillStyle = colorCss.textColorOfDownloadOutfit;
  // ctx.font = `${minorFontSize}px ${FONT_FAMILY}`;
  // metrics = ctx.measureText(`${femaleScoreCaptionStr}`);
  // ctx.fillText(
  //   `${femaleScoreCaptionStr}`,
  //   widthOfResult * ((100 - HORIZONTAL_PADDING_PERCENT) / 100) - metrics.width,
  //   widthOfResult * ((100 - VERTICAL_PADDING_PERCENT) / 100) -
  //     widthOfResult * (CONTENT_HEIGHT_PERCENT_SCORE / 100)
  // );
  // /** 최하단 2번째 스코어 캡션 끝 */

  /** 최하단 1번째 스코어 시작 */
  ctx.fillStyle = colorCss.textColorOfDownloadOutfit;
  ctx.font = `${MAJOR_TEXT_WEIGHT} ${majorFontSize}px ${FONT_FAMILY}`;
  metrics = ctx.measureText(`${scoreNum}`);
  ctx.fillText(
    `${scoreNum}`,
    widthOfResult * ((100 - HORIZONTAL_PADDING_PERCENT) / 100) - metrics.width,
    heightOfResult * ((100 - VERTICAL_PADDING_PERCENT) / 100) -
      minorFontSize -
      heightOfResult * (PADDING_BETWEEN_MAJOR_AND_MINOR_PERCENT / 100)
  );
  /** 최하단 1번째 스코어 끝 */

  /** 최하단 1번째 스코어 캡션 시작 */
  ctx.fillStyle = colorCss.textColorOfDownloadOutfit;
  ctx.font = `${MINOR_TEXT_WEIGHT} ${minorFontSize}px ${FONT_FAMILY}`;
  metrics = ctx.measureText(`${scoreCaptionStr}`);
  ctx.fillText(
    `${scoreCaptionStr}`,
    widthOfResult * ((100 - HORIZONTAL_PADDING_PERCENT) / 100) - metrics.width,
    heightOfResult * ((100 - VERTICAL_PADDING_PERCENT) / 100)
  );
  /** 최하단 1번째 스코어 캡션 끝 */
};

const _setBrandNameOnCanvas = ({ ctx, itemBrandList }) => {
  let metrics;

  const widthOfResult = RESULT_IMG_WIDTH_PX;
  const heightOfResult = widthOfResult / RESULT_IMG_RATIO;
  const majorFontSize = _getMajorTextFontSize({
    widthOfResult,
    scoreMode: false
  });
  const minorFontSize = _getMinorTextFontSize({ widthOfResult });

  for (let i = 0; i < itemBrandList.length; i++) {
    const element = itemBrandList[itemBrandList.length - 1 - i];

    /** 브랜드 텍스트 */
    ctx.fillStyle = colorCss.textColorOfDownloadOutfit;
    ctx.font = `${MAJOR_TEXT_WEIGHT} ${majorFontSize}px ${FONT_FAMILY}`;
    metrics = ctx.measureText(`${element.brand}`);
    ctx.fillText(
      `${element.brand}`,
      widthOfResult * ((100 - HORIZONTAL_PADDING_PERCENT) / 100) -
        metrics.width,
      heightOfResult * ((100 - VERTICAL_PADDING_PERCENT) / 100) -
        minorFontSize -
        heightOfResult * (PADDING_BETWEEN_MAJOR_AND_MINOR_PERCENT / 100) -
        heightOfResult * (CONTENT_HEIGHT_PERCENT_BRAND / 100) * i
    );

    /** 카테고리 텍스트 */
    ctx.fillStyle = colorCss.textColorOfDownloadOutfit;
    ctx.font = `${MINOR_TEXT_WEIGHT} ${minorFontSize}px ${FONT_FAMILY}`;
    metrics = ctx.measureText(`${element.category}`);
    ctx.fillText(
      `${element.category}`,
      widthOfResult * ((100 - HORIZONTAL_PADDING_PERCENT) / 100) -
        metrics.width,
      heightOfResult * ((100 - VERTICAL_PADDING_PERCENT) / 100) -
        heightOfResult * (CONTENT_HEIGHT_PERCENT_BRAND / 100) * i
    );
  }
};

const renderOutfitImage = async ({
  imageSrc,
  dateCaptionStr,
  usernameStr,
  heartCaption1Str,
  heartCaption2Str,
  heartCaption3Str,
  scoreNum,
  scoreCaptionStr,
  itemBrandList
}) => {
  /**
   * 아래 값이 전부 null이 아니면 스코어 적힌 하트 포함된 이미지 생성함
   *
   * heartCaption1Str,
   * heartCaption2Str,
   * heartCaption3Str,
   * scoreNum,
   * scoreCaptionStr,
   */
  if (!imageSrc) return null;

  let scoreMode = false;
  if (
    !!dateCaptionStr &&
    !!heartCaption1Str &&
    !!heartCaption2Str &&
    !!heartCaption3Str &&
    !!scoreNum &&
    !!scoreCaptionStr
  ) {
    scoreMode = true;
  }

  const canvas = document.createElement("canvas");
  canvas.width = RESULT_IMG_WIDTH_PX;
  canvas.height = RESULT_IMG_WIDTH_PX / RESULT_IMG_RATIO;

  let ctx = canvas.getContext("2d");

  _setCanvasWhite({ ctx });

  if (TEST_MODE) _setViewportForTest({ ctx });

  const outfitElem = await _makeImageElement(imageSrc);
  _setOutfitImgOnCanvas({ ctx, outfitElem });

  if (scoreMode) {
    /**
     * 예전에는 스코어모드 아니면 랜덤하트 배치용으로 사용했지만
     * 지금은 안쓰이는 기능
     */
    _setHeartOnCanvas({ ctx, scoreMode });
  }

  _setLogoOnCanvas({ ctx });

  /** 하단 텍스트는 하트보다 나중에 출력되어야 한다. (하트와 겹칠경우 텍스트가 상위에 노출되도록.) */
  _setInfoTextOnCanvas({ ctx, dateCaptionStr, usernameStr });

  if (scoreMode) {
    _setScoreOnCanvas({
      ctx,
      heartCaption1Str,
      heartCaption2Str,
      heartCaption3Str,
      scoreNum,
      scoreCaptionStr
    });
  } else if (itemBrandList.length !== 0) {
    _setBrandNameOnCanvas({ ctx, itemBrandList });
  }

  return {
    src: canvas.toDataURL(RESULT_IMG_TYPE, RESULT_IMG_QUALITY),
    filename: `MAUNENT-${_getNowTimeStr()}.jpeg`
  };
};

export { renderOutfitImage };
